import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Save, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CashoutSettingsPanel = () => {
    const [methods, setMethods] = useState([]);
    const [supportPhone, setSupportPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/cashout-settings`, { withCredentials: true });
            setMethods(data.methods || []);
            setSupportPhone(data.support_phone || '');
            setDirty(false);
        } catch {
            toast.error('Failed to load cashout settings');
        }
        setLoading(false);
    };

    useEffect(() => { fetchSettings(); }, []);

    const handleToggle = (id) => {
        setMethods(methods.map(m => m.id === id ? { ...m, enabled: !m.enabled } : m));
        setDirty(true);
    };

    const handleSave = async () => {
        if (!supportPhone.trim()) { toast.error('Support phone cannot be empty'); return; }
        if (!methods.some(m => m.enabled)) { toast.error('At least one payout method must be enabled'); return; }
        setSaving(true);
        try {
            await axios.put(`${API}/admin/cashout-settings`, {
                support_phone: supportPhone.trim(),
                methods,
            }, { withCredentials: true });
            toast.success('Cashout settings saved');
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save settings');
        }
        setSaving(false);
    };

    const enabledCount = methods.filter(m => m.enabled).length;

    return (
        <div className="card-dark p-6 mb-6 border-[#3B82F6]/20" data-testid="cashout-settings-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <SettingsIcon className="w-4 h-4 text-[#3B82F6]" />
                    <h3 className="text-base font-bold text-white">Cashout Settings</h3>
                    <span className="text-[10px] text-[#A0AAB2] ml-1">
                        {enabledCount} of {methods.length} method(s) enabled
                    </span>
                </div>
                {dirty && (
                    <Button onClick={handleSave} disabled={saving}
                        size="sm" className="bg-[#FFD700] text-black font-bold h-8"
                        data-testid="cashout-settings-save-btn">
                        <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving…' : 'Save Changes'}
                    </Button>
                )}
            </div>

            {loading ? (
                <p className="text-[#A0AAB2] text-xs text-center py-3">Loading…</p>
            ) : (
                <div className="space-y-5">
                    {/* Support Phone */}
                    <div>
                        <Label className="text-[#A0AAB2] text-xs flex items-center gap-1.5 mb-1">
                            <Phone className="w-3 h-3" /> Support Phone (shown to players after cashout)
                        </Label>
                        <Input
                            value={supportPhone}
                            onChange={(e) => { setSupportPhone(e.target.value); setDirty(true); }}
                            placeholder="e.g. 256-746-3933"
                            className="bg-[#0a0a0a] border-white/10 text-white max-w-xs"
                            data-testid="cashout-settings-phone-input"
                        />
                        <p className="text-[10px] text-[#666] mt-1">
                            Players will see this number in the &ldquo;Request Received&rdquo; dialog and on Cash payout instructions.
                        </p>
                    </div>

                    {/* Payout Methods */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-[#A0AAB2] text-xs">
                                Available Payout Methods
                            </Label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMethods(methods.map(m => ({ ...m, enabled: true })));
                                        setDirty(true);
                                    }}
                                    className="text-[10px] font-bold px-2 py-1 rounded border border-white/20 text-white/70 hover:bg-white/5 transition"
                                    data-testid="cashout-settings-enable-all-btn"
                                    title="Turn every payout method back on"
                                >
                                    Enable all
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {methods.map(m => (
                                <div key={m.id}
                                    className={`flex items-center justify-between p-3 rounded-lg border ${
                                        m.enabled ? 'bg-[#0a0a0a] border-[#39FF14]/30' : 'bg-[#0a0a0a]/50 border-white/10 opacity-60'
                                    }`}
                                    data-testid={`cashout-method-row-${m.id.replace(/\s+/g, '-')}`}>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white text-sm font-bold truncate">{m.label}</p>
                                        <p className="text-[10px] text-[#A0AAB2] truncate">
                                            {m.needs_handle ? 'Player must enter handle' : 'No handle required (in-person)'}
                                        </p>
                                    </div>
                                    <Switch
                                        checked={m.enabled}
                                        onCheckedChange={() => handleToggle(m.id)}
                                        data-testid={`cashout-method-toggle-${m.id.replace(/\s+/g, '-')}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-[#666] mt-2">
                            Disabled methods are hidden from players&rsquo; Cash Out dialog. At least one method must stay enabled.
                        </p>
                    </div>

                    {!dirty && (
                        <Button onClick={fetchSettings} variant="outline" size="sm"
                            className="border-white/20 text-white text-xs h-7">
                            Reload
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};
